import Popup from "../Elements/Popup";
import Card from "./Card";
import { useContext, useState, useEffect } from "react";
import { YearContext, AuthContext } from "../../context/context";
import { TbQuote, TbEdit, TbCirclePlus, TbTrash, TbPlus } from "react-icons/tb";
import { submitQuote, deleteQuote, updateQuote } from "../../api";
import InputTextarea from "../Inputs/InputTextarea";
import animation from "../../Animations/write-animation.json";
import { useLanguage } from "../../context/LanguageContext";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Toast from "../Elements/Toast";
import CardMobile from "./CardMobile";

const quoteSchema = z.object({
  quote: z
    .string()
    .min(1, "Quote cannot be empty")
    .max(100, "Quote must be 100 characters or less"),
});

// Simple hook to detect mobile (md breakpoint)
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

const CardQuote = () => {
  const { selectedYear } = useContext(YearContext);
  const { userData, setUserData } = useContext(AuthContext);
  const { copy } = useLanguage();

  const [popupType, setPopupType] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const openPopup = (type) => setPopupType(type);
  const closePopup = () => {
    reset();
    setPopupType(null);
    setQuoteEditError("");
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(quoteSchema),
    defaultValues: { quote: "" },
  });

  const [quoteToEdit, setQuoteToEdit] = useState(null);
  const [editedQuoteText, setEditedQuoteText] = useState("");
  const [quoteEditError, setQuoteEditError] = useState("");

  const quoteData = selectedYear
    ? userData?.quotes?.filter(
        (item) => Number(item.year) === Number(selectedYear)
      )
    : userData?.quotes;

  const handleSubmitQuote = async (data) => {
    try {
      const newQuote = await submitQuote(data.quote, selectedYear);

      if (!newQuote || !newQuote.id) {
        setToast({
          message: copy.card_quote.errors.invalid_response,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.quotes = [...(newUserData.quotes || []), newQuote];
      setUserData(newUserData);

      setToast({
        message: copy.card_quote.errors.update_success,
        type: "success",
      });
      reset();
      closePopup();
    } catch (err) {
      console.error("Error submitting quote:", err.message);
      setToast({ message: copy.card_quote.errors.submit_error, type: "error" });
    }
  };

  const [quoteIdToDelete, setQuoteIdToDelete] = useState(null);

  const handleDeleteClickQuote = (quoteId) => {
    setQuoteIdToDelete(quoteId);
    openPopup("quote-delete");
  };

  const handleDeleteQuote = async () => {
    if (!quoteIdToDelete) {
      setToast({ message: copy.card_quote.errors.not_found, type: "error" });
      return;
    }

    try {
      const result = await deleteQuote(quoteIdToDelete);
      if (!result) {
        setToast({
          message: copy.card_quote.errors.delete_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.quotes = newUserData.quotes.filter(
        (q) => q.id !== quoteIdToDelete
      );
      setUserData(newUserData);

      setToast({
        message: copy.card_quote.errors.delete_success,
        type: "success",
      });
      setQuoteIdToDelete(null);
      closePopup();
    } catch (err) {
      console.error("Error deleting quote:", err.message);
      setToast({
        message: copy.card_quote.errors.delete_failed,
        type: "error",
      });
    }
  };

  const handleEditClickQuote = (quoteId, quoteText) => {
    setQuoteToEdit(quoteId);
    setEditedQuoteText(quoteText);
    openPopup("quote-edit");
  };

  const handleEditQuote = async () => {
    const result = quoteSchema.safeParse({ quote: editedQuoteText });

    if (!result.success) {
      const message =
        result.error.format().quote?._errors?.[0] ||
        copy.card_quote.errors.empty;
      setQuoteEditError(message);
      return;
    }

    try {
      const updatedQuote = await updateQuote(quoteToEdit, {
        quote: editedQuoteText,
      });

      if (!updatedQuote || !updatedQuote.id) {
        setToast({
          message: copy.card_quote.errors.update_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.quotes = newUserData.quotes.map((q) =>
        q.id === quoteToEdit ? { ...q, quote: updatedQuote.quote } : q
      );
      setUserData(newUserData);

      setToast({
        message: copy.card_quote.errors.update_success,
        type: "success",
      });
      setEditedQuoteText("");
      setQuoteToEdit(null);
      closePopup();
    } catch (err) {
      console.error("Error updating quote:", err.message);
      setToast({
        message: copy.card_quote.errors.update_failed,
        type: "error",
      });
    }
  };

  const addQuoteDropdown = [
    {
      text: copy.dropdown.add,
      onClick: () => openPopup("quote"),
      icon: <TbCirclePlus />,
    },
  ];

  const quoteDropdown = (quoteId, quoteText) => [
    {
      text: copy.dropdown.edit,
      onClick: () => handleEditClickQuote(quoteId, quoteText),
      icon: <TbEdit />,
    },
    {
      text: copy.dropdown.delete,
      onClick: () => handleDeleteClickQuote(quoteId),
      icon: <TbTrash />,
    },
  ];

  const isMobile = useIsMobile();

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      {/* Mobile Card */}
      {isMobile &&
        (quoteData && quoteData.length > 0 ? (
          <CardMobile
            title={copy.card_quote.headline}
            dropdownOptions={quoteDropdown(quoteData[0].id, quoteData[0].quote)}
            action={
              <button
                className="btn-secondary-m w-full"
                onClick={() =>
                  handleEditClickQuote(quoteData[0].id, quoteData[0].quote)
                }
              >
                {copy.dropdown.edit}
              </button>
            }
          >
            <p className="w-full text-left italic text-base mt-2 mb-1">
              &quot;{quoteData[0].quote}&quot;
            </p>
          </CardMobile>
        ) : (
          <CardMobile
            title={copy.card_quote.headline}
            dropdownOptions={addQuoteDropdown}
            contentBgColor="bg-blue-100"
          >
            <div className="py-4">
              <button
                className="w-10 h-10 rounded-md flex items-center justify-center shadow-sm bg-white card-base-color transition-colors duration-200 mt-2 mx-auto"
                onClick={() => openPopup("quote")}
                aria-label={copy.card_quote.add_button}
              >
                <TbPlus className="text-2xl" />
              </button>
            </div>
          </CardMobile>
        ))}

      {/* Desktop Card(s) */}
      {!isMobile &&
        (quoteData && quoteData.length > 0 ? (
          quoteData.map((quoteItem) => (
            <Card
              key={quoteItem.id}
              iconTop={TbQuote}
              title={copy.card_quote.headline}
              description={copy.card_quote.description}
              options={quoteDropdown(quoteItem.id, quoteItem.quote)}
              onClick={() => openPopup("quote")}
              cardClassName="h-full"
            >
              <div className="flex flex-col w-full h-full justify-center gap-1 px-4">
                <p className="text-lg font-headline text-center font-normal italic">
                  &quot;{quoteItem.quote}&quot;
                </p>
              </div>
            </Card>
          ))
        ) : (
          <Card
            iconTop={TbQuote}
            title={copy.card_quote.headline}
            description={copy.card_quote.description}
            lottieAnimation={animation}
            startButton={`${copy.card_quote.add_button} ${selectedYear}`}
            onClick={() => openPopup("quote")}
            options={addQuoteDropdown}
            className="justify-center"
            cardClassName="h-full"
          />
        ))}

      {/* Add Quote Popup */}
      <Popup
        isOpen={popupType === "quote"}
        onClose={closePopup}
        title={`${copy.card_quote.popup.add_title} ${selectedYear}`}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={() => {
          document
            .getElementById("quote-form")
            ?.dispatchEvent(
              new Event("submit", { bubbles: true, cancelable: true })
            );
        }}
      >
        <form id="quote-form" onSubmit={handleSubmit(handleSubmitQuote)}>
          <InputTextarea
            placeholder={copy.card_quote.form.placeholder}
            label={copy.card_quote.form.label}
            {...register("quote")}
            error={errors.quote?.message}
            details={`${watch("quote")?.length || 0}/100 characters`}
          />
        </form>
      </Popup>

      {/* Delete Quote Popup */}
      <Popup
        isOpen={popupType === "quote-delete"}
        onClose={closePopup}
        title={copy.card_quote.popup.delete_title}
        description={copy.card_quote.popup.delete_description}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.delete}
        onClickPrimaryLink={handleDeleteQuote}
      />

      {/* Edit Quote Popup */}
      <Popup
        isOpen={popupType === "quote-edit"}
        onClose={closePopup}
        title={copy.card_quote.popup.edit_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={handleEditQuote}
      >
        <div className="flex flex-col gap-4">
          <InputTextarea
            placeholder={copy.card_quote.form.placeholder}
            label={copy.card_quote.form.label}
            value={editedQuoteText}
            onChange={(e) => setEditedQuoteText(e.target.value)}
            error={quoteEditError}
            details={`${editedQuoteText.length}/100 characters`}
          />
        </div>
      </Popup>
    </>
  );
};

export default CardQuote;
